<script setup>
import { computed, provide, watch } from "vue";
import { useChartDisplay } from "@/composables/useChartDisplay";
import { useVerbatims } from "@/composables/useVerbatims";
import { Badge } from "@/components/ui/badge";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useResultsV2RecodesStore } from "@stores/survey/resultsV2/resultsV2.recodes";
import { useResultsV2CardsStore } from "@stores/survey/resultsV2/resultsV2.cards";
import ResultsBarChart from "../Charts/ResultsBarChart.vue";
import ResultsStackedChart from "../Charts/ResultsStackedChart.vue";
import ResultsPieChart from "../Charts/ResultsPieChart.vue";
import ResultsHeatmapChart from "../Charts/ResultsHeatmapChart.vue";
import ResultsRadarChart from "../Charts/ResultsRadarChart.vue";
import ResultsPodiumChart from "../Charts/ResultsPodiumChart.vue";
import ResultsDotPlotChart from "../Charts/ResultsDotPlotChart.vue";
import PopulationSwitch from "./PopulationSwitch.vue";
import ResultsDataTable from "../Charts/ResultsDataTable.vue";
import ResultsVerbatimsList from "../Charts/ResultsVerbatimsList.vue";
import ResultsStyledBlock from "../ResultsStyledBlock.vue";
import ResultsV2CardIndicators from "./ResultsV2CardIndicators.vue";
import ResultsCardToolbar from "./ResultsCardToolbar.vue";
import { LoadingState } from "@/components/episto-ui/loading-state";
import { recodeFormComponent } from "../recodeFormComponent";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const cardRef = computed(() => props.card);
const {
  mergedOptions,
  questionMeta,
  cardType,
  populationsBarData,
  isMatrix,
  matrixItemsBarData,
  matrixTableData,
  stackedData,
  matrixChoiceCodes,
  matrixChoiceLabels,
  pieData,
  radarAxes,
  radarSeries,
  radarScale,
  respondents,
  populationRespondents,
  displayedPopulations,
  singlePopulation,
  shouldIncludeWeights,
  activeIteration,
  downloadCardAsImage,
  copyCardToClipboard,
  copied,
  customBaseLabel,
  displayMode,
  isBar,
  isStacked,
  isPie,
  isHeatmap,
  isRadar,
  isPodium,
  isDotPlot,
  isSingleSeries,
  isHorizontal,
  isDoughnut,
  showSignificance,
  cardComment,
  displayName,
  displayTitle,
} = useChartDisplay(cardRef);
provide("cardPopulations", displayedPopulations);

const showPopulationSwitch = computed(
  () => isSingleSeries.value && displayMode.value === "chart" && displayedPopulations.value.length > 1,
);
const singlePopulationStackedData = computed(() =>
  stackedData.value.filter((row) => row.popId === singlePopulation.value?.id),
);
const singlePopulationBarData = computed(
  () => populationsBarData.value.find((pop) => pop.popId === singlePopulation.value?.id) || populationsBarData.value[0] || null,
);
const legendPopulations = computed(() =>
  showPopulationSwitch.value
    ? populationRespondents.value.filter((pop) => pop.token === singlePopulation.value?.token)
    : populationRespondents.value,
);

// Free-text recodes (open AI recode, contact attribute open, URL parameter without
// categories) hold one value per respondent, so they are shown as verbatims rather than a chart.
const { isOpenRecode, isOpenQuestion, verbatimsQuestionId } = useVerbatims({
  cardType,
  questionMeta,
  activeIteration,
});
const isFreeTextRecode = computed(() => isOpenRecode.value || isOpenQuestion.value);

const shownMode = computed(() => (isFreeTextRecode.value ? "verbatims" : displayMode.value));

const recodesStore = useResultsV2RecodesStore();
const cardsStore = useResultsV2CardsStore();

const showCounts = computed(() => mergedOptions.value.show_counts ?? true);

// Config mode
const configMode = computed(() => cardsStore.isConfiguring(props.card.id));

const recodeToken = computed(() => useResultsV2Store().recodeTokenForViewCard(props.card));

const computing = computed(() => !!recodesStore.loadingRecodes[recodeToken.value]);

watch(configMode, (open) => {
  if (open && !props.card._isNew && recodeToken.value) recodesStore.initExistingRecode(recodeToken.value);
});

function closeConfig() {
  cardsStore.closeConfig(props.card.id);
}

// Dynamic form component routing
const recodeKind = computed(() => {
  if (props.card._recodeKind) return props.card._recodeKind;
  return recodesStore.recodeConfigs[recodeToken.value]?.recodeKind || "conditions";
});

const formComponent = computed(() => recodeFormComponent(recodeKind.value));

function handleSave() {
  // Close form immediately — card will show spinner until ActionCable completes
  closeConfig();
}

function handleCancel() {
  if (props.card._isNew && props.card._recodeToken) {
    recodesStore.cleanup(recodeToken.value);
    closeConfig();
    cardsStore.removeCard(props.card.id);
  } else {
    recodesStore.cleanup(recodeToken.value);
    closeConfig();
  }
}

function handleDelete() {
  recodesStore.deleteRecode(recodeToken.value);
}

</script>

<template>
  <!-- Config mode: show recode form -->
  <component
    :is="formComponent"
    v-if="configMode && recodeToken"
    :card-token="recodeToken"
    :is-new="!!card._isNew"
    @save="handleSave"
    @cancel="handleCancel"
    @delete="handleDelete" />

  <!-- Chart mode: show recode charts -->
  <div
    v-else
    class="flex flex-col gap-3 p-4">
    <div class="flex items-start justify-between gap-2">
      <div class="flex-1 min-w-0 flex flex-col gap-2.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold truncate">{{ displayName }}</span>
          <Badge variant="outline" class="shrink-0 text-3xs px-1.5 py-0 font-normal text-muted-foreground">
            Recode
          </Badge>
        </div>
        <p v-if="displayTitle" class="text-sm font-semibold leading-snug truncate">
          {{ displayTitle }}
        </p>
        <div v-if="populationRespondents.length > 1" class="flex flex-wrap items-center gap-1 text-xs text-muted-foreground tabular-nums">
          <template v-for="(pop, idx) in legendPopulations" :key="pop.token">
            <span v-if="idx > 0" class="w-1" />
            <span class="inline-block h-2 w-2 shrink-0 rounded-sm" :style="{ backgroundColor: pop.color }" />
            <span>n={{ pop.respondents }}</span>
          </template>
          <ResultsV2CardIndicators :base-label="customBaseLabel" :weighted="shouldIncludeWeights" class="ml-1" />
        </div>
        <span v-else-if="respondents" class="text-xs text-muted-foreground tabular-nums">
          n={{ respondents }}
          <ResultsV2CardIndicators :base-label="customBaseLabel" :weighted="shouldIncludeWeights" />
        </span>
      </div>
      <div v-if="showPopulationSwitch" class="card-toolbar print:hidden">
        <PopulationSwitch :card="card" :populations="displayedPopulations" :selected="singlePopulation.token" />
      </div>
      <ResultsCardToolbar
        :card="card"
        :readonly="readonly"
        :display-mode="shownMode"
        :copied="copied"
        show-copy
        show-download
        @copy="copyCardToClipboard(card.id)"
        @download="downloadCardAsImage(card.id, displayName)" />
    </div>

    <!-- Computing spinner -->
    <LoadingState v-if="computing" label="Computing..." size="sm" class="h-32" />

    <template v-else-if="questionMeta">
      <!-- Open recode: free-text values per respondent, shown as verbatims -->
      <div v-if="shownMode === 'verbatims'">
        <ResultsVerbatimsList :question-id="verbatimsQuestionId" :card-type="cardType" />
      </div>

      <template v-else-if="shownMode === 'chart'">
        <div v-if="isMatrix && isBar" class="flex flex-col gap-5">
          <div v-for="item in matrixItemsBarData" :key="item.itemId" class="flex flex-col gap-1.5">
            <span class="text-xs font-semibold text-foreground">
              {{ item.itemLabel }}
              <span class="font-normal text-muted-foreground">(N={{ item.itemN }})</span>
            </span>
            <ResultsBarChart :populations="item.populations" :horizontal="isHorizontal" :show-significance="showSignificance" />
          </div>
        </div>
        <ResultsBarChart v-else-if="isBar" :populations="populationsBarData" :horizontal="isHorizontal" :show-significance="showSignificance" />
        <ResultsStackedChart
          v-else-if="isStacked"
          :data="stackedData"
          :choice-codes="matrixChoiceCodes"
          :choice-labels="matrixChoiceLabels"
          :horizontal="isHorizontal"
          :show-significance="showSignificance"
        />
        <ResultsPieChart v-else-if="isPie" :data="pieData" :donut="isDoughnut" />
        <ResultsHeatmapChart
          v-else-if="isHeatmap"
          :data="singlePopulationStackedData"
          :choice-codes="matrixChoiceCodes"
          :choice-labels="matrixChoiceLabels"
          :accent="mergedOptions.color || singlePopulation?.color"
        />
        <template v-else-if="isRadar">
          <ResultsRadarChart
            v-if="radarAxes.length >= 3"
            :axes="radarAxes"
            :series="radarSeries"
            :min="radarScale.min"
            :max="radarScale.max"
          />
          <p v-else class="flex h-40 items-center justify-center text-center text-13 text-muted-foreground">
            A radar needs a matrix with at least 3 items.
          </p>
        </template>
        <ResultsPodiumChart
          v-else-if="isPodium"
          :population="singlePopulationBarData" />
        <ResultsDotPlotChart
          v-else-if="isDotPlot"
          :populations="populationsBarData"
          :show-significance="showSignificance" />
      </template>

      <ResultsDataTable
        v-else
        :populations="populationsBarData"
        :matrix-items="matrixTableData"
        :show-significance="showSignificance"
        :show-counts="showCounts"
      />

      <!-- Annotation -->
      <ResultsStyledBlock
        v-if="cardComment"
        :html="cardComment"
        :variant="mergedOptions.comment_style || 'info'"
        class="mt-4"
      />
    </template>

    <div v-else class="flex h-32 items-center justify-center text-sm text-muted-foreground">
      {{ recodeToken && !card.card_id ? 'No data' : `Recode not found (${card.card_id})` }}
    </div>
  </div>
</template>
